import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Star, Heart, Users, Check, MessageCircle, Send, X } from 'lucide-react';
import { ChatBubble } from '../ui/chat-bubble';
import { ImageViewer } from '../ui/image-viewer';
import { supabase } from '../../lib/supabase';
import { CandidateWithSlides, Evaluator, Evaluation, EvaluationComment } from '../../lib/supabase';
import { collectRecipientsForTopic, recipientsForNewTopic } from '../../lib/notificationRecipients';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

interface EvaluationDetailProps {
  candidate: CandidateWithSlides;
  evaluator: Evaluator;
  onBack: () => void;
  onEvaluationSubmitted: () => void;
  isOwn?: boolean; // 自分の研究かどうか
}

// Send notification to candidate (fire-and-forget, but log failures for debugging)
async function sendNotification(eventType: string, candidateId: string, actorName: string, commentContent?: string, recipientEmail?: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    fetch('/api/send-notification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ event_type: eventType, candidate_id: candidateId, actor_name: actorName, comment_content: commentContent, recipient_email: recipientEmail }),
      keepalive: true, // survive page navigation / tab close mid-request
    })
      .then(async (r) => {
        if (!r.ok) {
          const text = await r.text().catch(() => '');
          console.warn(`[notify] ${eventType} failed: HTTP ${r.status} ${text}`);
        }
      })
      .catch((err) => console.warn(`[notify] ${eventType} network error:`, err));
  } catch (err) {
    console.warn(`[notify] ${eventType} setup error:`, err);
  }
}

export default function EvaluationDetail({
  candidate,
  evaluator,
  onBack,
  onEvaluationSubmitted,
  isOwn = false,
}: EvaluationDetailProps) {
  const [liked, setLiked] = useState(false);
  const [wantToParticipate, setWantToParticipate] = useState(false);
  const [savingLike, setSavingLike] = useState(false);
  const [savingParticipate, setSavingParticipate] = useState(false);
  const [existingEvaluation, setExistingEvaluation] = useState<Evaluation | null>(null);
  const [rating, setRating] = useState<number>(0);
  const [allEvaluations, setAllEvaluations] = useState<Evaluation[]>([]);
  const [comments, setComments] = useState<EvaluationComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [showNewCommentForm, setShowNewCommentForm] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [commentAuthors, setCommentAuthors] = useState<{[key: string]: {name: string}}>({});
  const [evaluatorById, setEvaluatorById] = useState<Record<string, {name: string; avatar_url?: string; university?: string; is_reviewer?: boolean}>>({});
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [collapsedTopics, setCollapsedTopics] = useState<Record<string, boolean>>({});
  const cancelledRef = useRef(false);

  // Debounced notification scheduler for like / participate toggles.
  // Goal: avoid hitting Resend's 2 req/sec limit when users tick multiple
  // checkboxes in quick succession, and avoid notifying on ON→OFF→ON bounces.
  // Per-toggle debounced notification scheduler for like / participate.
  // Each checkbox is tracked independently so that toggling one never
  // re-sends a notification for the other (they are unrelated events).
  // For a single checkbox, the last state within the debounce window wins:
  // an OFF→ON→OFF bounce sends nothing, OFF→ON sends one notification.
  const NOTIFY_DEBOUNCE_MS = 3000;

  type NotifyKind = 'like' | 'participate';
  // Pending state per kind: timer + whether the toggle ended up ON.
  const notifyTimersRef = useRef<Record<NotifyKind, ReturnType<typeof setTimeout> | null>>({
    like: null,
    participate: null,
  });
  const notifyPendingRef = useRef<Record<NotifyKind, boolean>>({
    like: false,
    participate: false,
  });
  const notifyHasPendingRef = useRef<Record<NotifyKind, boolean>>({
    like: false,
    participate: false,
  });

  const flushNotification = (kind: NotifyKind) => {
    const timer = notifyTimersRef.current[kind];
    if (timer) {
      clearTimeout(timer);
      notifyTimersRef.current[kind] = null;
    }
    if (!notifyHasPendingRef.current[kind]) return;
    const isOn = notifyPendingRef.current[kind];
    notifyHasPendingRef.current[kind] = false;
    // Only notify when the toggle ended up ON (turning it off sends nothing).
    if (isOn) sendNotification(kind, candidate.id, evaluator.name);
  };

  const flushPendingNotifications = () => {
    flushNotification('like');
    flushNotification('participate');
  };

  const scheduleNotification = (next: { liked?: boolean; wantToParticipate?: boolean }) => {
    if (next.liked !== undefined) {
      notifyPendingRef.current.like = next.liked;
      notifyHasPendingRef.current.like = true;
      if (notifyTimersRef.current.like) clearTimeout(notifyTimersRef.current.like);
      notifyTimersRef.current.like = setTimeout(() => flushNotification('like'), NOTIFY_DEBOUNCE_MS);
    }
    if (next.wantToParticipate !== undefined) {
      notifyPendingRef.current.participate = next.wantToParticipate;
      notifyHasPendingRef.current.participate = true;
      if (notifyTimersRef.current.participate) clearTimeout(notifyTimersRef.current.participate);
      notifyTimersRef.current.participate = setTimeout(() => flushNotification('participate'), NOTIFY_DEBOUNCE_MS);
    }
  };

  // On unmount, flush any pending notification immediately so we don't drop
  // the user's intent. fetch keepalive: true (set in sendNotification) lets
  // the request survive even if the page is closing.
  useEffect(() => {
    return () => {
      // flushPendingNotifications is a no-op for kinds with nothing pending.
      flushPendingNotifications();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    loadExistingEvaluation();
    loadAllEvaluations();
    // Mark notifications as read
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (cancelledRef.current) return;
        if (user?.email) {
          await supabase
            .from('notification_queue')
            .update({ read_at: new Date().toISOString() })
            .eq('recipient_email', user.email)
            .eq('candidate_id', candidate.id)
            .is('read_at', null);
        }
      } catch {}
    })();
    return () => { cancelledRef.current = true; };
  }, []);

  useEffect(() => {
    // Load comments when we have any evaluations, not just the current user's
    if (allEvaluations.length > 0) {
      loadComments();
    }
  }, [allEvaluations]);

  // Auto-refresh comments every 30 seconds
  useEffect(() => {
    if (allEvaluations.length === 0) return;
    const interval = setInterval(() => {
      if (!commentLoading) loadComments();
    }, 30000);
    return () => clearInterval(interval);
  }, [allEvaluations, commentLoading]);

  const loadExistingEvaluation = async () => {
    const isCancelled = () => cancelledRef.current;
    try {
      const { data } = await supabase
        .from('evaluations')
        .select('*')
        .eq('candidate_id', candidate.id)
        .eq('evaluator_id', evaluator.id)
        .maybeSingle();

      if (isCancelled()) return;
      if (data) {
        setExistingEvaluation(data);
        setLiked(data.liked);
        setWantToParticipate(data.want_to_participate);
        setRating(Math.max(0, Math.min(5, data.rating || 0)));
      }
    } catch (err) {
      // No existing evaluation is fine
    }
  };

  // Ensure an evaluation row exists; if not, create with minimal defaults
  const ensureEvaluation = async (): Promise<Evaluation> => {
    if (existingEvaluation) return existingEvaluation;
    const { data } = await supabase
      .from('evaluations')
      .select('*')
      .eq('candidate_id', candidate.id)
      .eq('evaluator_id', evaluator.id)
      .maybeSingle();
    if (data) {
      setExistingEvaluation(data as Evaluation);
      setRating(Math.max(0, Math.min(5, (data as any).rating || 0)));
      return data as Evaluation;
    }
    const { data: created, error } = await supabase
      .from('evaluations')
      .insert({
        candidate_id: candidate.id,
        evaluator_id: evaluator.id,
        rating: 0, // 0 = 評価なし
        liked: false,
        want_to_participate: false,
      })
      .select()
      .single();
    if (error) throw error;
    setExistingEvaluation(created as Evaluation);
    setRating(0);
    // ensure comments area becomes available
    setAllEvaluations(prev => prev.length ? prev : [created as Evaluation]);
    return created as Evaluation;
  };

  const savePartial = async (patch: Partial<Evaluation>) => {
    const evalRow = await ensureEvaluation();
    const { error } = await supabase
      .from('evaluations')
      .update({ ...patch })
      .eq('id', evalRow.id);
    if (error) throw error;
    setExistingEvaluation({ ...evalRow, ...patch } as Evaluation);
    if (typeof patch.rating === 'number') {
      setRating(Math.max(0, Math.min(5, patch.rating)));
    }
  };

  const loadAllEvaluations = async () => {
    const isCancelled = () => cancelledRef.current;
    try {
      // Load all evaluations for this candidate to get comment access
      const { data } = await supabase
        .from('evaluations')
        .select('*')
        .eq('candidate_id', candidate.id);

      if (isCancelled()) return;
      if (data && data.length > 0) {
        setAllEvaluations(data);
        const evaluatorIds = Array.from(new Set((data as any[]).map(e => e.evaluator_id).filter(Boolean)));
        if (evaluatorIds.length) {
          const { data: infos } = await supabase.from('evaluators').select('id, name, avatar_url, university, is_reviewer').in('id', evaluatorIds);
          if (isCancelled()) return;
          const map: Record<string, {name: string; avatar_url?: string; university?: string; is_reviewer?: boolean}> = {};
          (infos || []).forEach((ev: any) => { map[ev.id] = { name: ev.name, avatar_url: ev.avatar_url || undefined, university: ev.university || undefined, is_reviewer: !!ev.is_reviewer }; });
          setEvaluatorById(map);
        }
      }
    } catch (err) {
      console.error('Error loading evaluations:', err);
    }
  };

  const loadComments = async () => {
    const isCancelled = () => cancelledRef.current;
    // Load comments if there are any evaluations; otherwise, keep empty list
    if (allEvaluations.length === 0) {
      if (!isCancelled()) setComments([]);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (isCancelled()) return;
      const uid = user?.id || null;
      const uemail = user?.email || null;
      currentUserIdRef.current = uid;
      setCurrentUserId(uid);
      setCurrentUserEmail(uemail);

      // Load comments from all evaluations for this candidate
      const evaluationIds = allEvaluations.map(e => e.id);
      const { data, error } = await supabase
        .from('evaluation_comments')
        .select('*')
        .in('evaluation_id', evaluationIds)
        .order('created_at', { ascending: true });
      if (isCancelled()) return;
      if (error) throw error;
      const visible = (data || []) as EvaluationComment[];
      setComments(visible);

      // Ensure evaluatorById has all evaluators referenced in comments
      const missingEvalIds = Array.from(new Set(visible.map(c => c.evaluation_id)))
        .filter(eid => {
          const ev = allEvaluations.find((e: any) => e.id === eid);
          return ev && !evaluatorById[ev.evaluator_id];
        });
      if (missingEvalIds.length > 0) {
        const missingEvaluatorIds = missingEvalIds
          .map(eid => (allEvaluations.find((e: any) => e.id === eid) as any)?.evaluator_id)
          .filter(Boolean);
        if (missingEvaluatorIds.length > 0) {
          const { data: extras } = await supabase.from('evaluators').select('id, name, avatar_url, university, is_reviewer').in('id', missingEvaluatorIds);
          if (isCancelled()) return;
          if (extras && extras.length > 0) {
            const updated = { ...evaluatorById };
            extras.forEach((ev: any) => { updated[ev.id] = { name: ev.name, avatar_url: ev.avatar_url || undefined, university: ev.university || undefined, is_reviewer: !!ev.is_reviewer }; });
            setEvaluatorById(updated);
          }
        }
      }
    } catch (err: any) {
      if (!isCancelled()) setError(err.message);
    }
  };

  const handleSubmitEvaluation = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      const evaluationData = {
        candidate_id: candidate.id,
        evaluator_id: evaluator.id,
        // rating: 0-5（0は「評価なし」）
        rating: Math.max(0, Math.min(5, rating)),
        liked,
        want_to_participate: wantToParticipate
      };

      if (existingEvaluation) {
        // Update existing evaluation
        const { data: updated, error } = await supabase
          .from('evaluations')
          .update(evaluationData)
          .eq('id', existingEvaluation.id)
          .select()
          .single();

        if (error) throw error;
        if (updated) {
          setExistingEvaluation(updated as Evaluation);
          setRating(Math.max(0, Math.min(5, (updated as any).rating || 0)));
        }
      } else {
        // Create new evaluation
        const { data: created, error } = await supabase
          .from('evaluations')
          .insert(evaluationData)
          .select()
          .single();

        if (error) throw error;
        if (created) {
          setExistingEvaluation(created as Evaluation);
          setRating(Math.max(0, Math.min(5, (created as any).rating || 0)));
        }
      }

      setSuccess(true);
      // keep aggregates fresh
      await loadAllEvaluations();
      setTimeout(() => {
        onEvaluationSubmitted();
        onBack();
      }, 1500);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() && !newTopic.trim()) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      // Ensure an evaluation exists for this evaluator-candidate pair
      let evaluationId = existingEvaluation?.id;
      if (!evaluationId) {
        // Try to find existing evaluation by this evaluator
        const { data: existing } = await supabase
          .from('evaluations')
          .select('id')
          .eq('candidate_id', candidate.id)
          .eq('evaluator_id', evaluator.id)
          .maybeSingle();
        if (existing) {
          evaluationId = existing.id as string;
        } else {
          // Create minimal evaluation record to attach comments
          const { data: created, error: evalError } = await supabase
            .from('evaluations')
            .insert({
              candidate_id: candidate.id,
              evaluator_id: evaluator.id,
              rating: 0,
              liked: false,
              want_to_participate: false
            })
            .select()
            .single();
          if (evalError) throw evalError;
          evaluationId = created.id as string;
          setExistingEvaluation(created as Evaluation);
          setAllEvaluations(prev => [...prev, created as Evaluation]);
        }
      }

      const topic = newTopic.trim() || 'general';
      const isExistingTopic = comments.some((c) => (c.topic ?? 'general') === topic);

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: evaluationId!,
          user_id: user.id,
          user_type: 'evaluator',
          author_name: evaluator.name,
          author_email: evaluator.email,
          content: newComment.trim() || `新しいトピック: ${newTopic.trim()}`,
          parent_comment_id: null,
          topic
        });

      if (error) throw error;

      const commentText = newComment.trim() || `新しいトピック: ${newTopic.trim()}`;
      const recipients = isExistingTopic
        ? collectRecipientsForTopic(comments, topic, candidate.email, evaluator.email)
        : recipientsForNewTopic(candidate.email, evaluator.email);
      recipients.forEach((email) => {
        sendNotification('comment', candidate.id, evaluator.name, commentText, email);
      });
      setNewComment('');
      setNewTopic('');
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleSubmitReply = async (parentId: string, topic: string) => {
    if (!replyContent.trim()) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      // Use existing evaluation if available, otherwise use the first evaluation
      const evaluationId = existingEvaluation?.id || allEvaluations[0].id;

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: evaluationId,
          user_id: user.id,
          user_type: 'evaluator',
          author_name: evaluator.name,
          author_email: evaluator.email,
          content: replyContent.trim(),
          parent_comment_id: parentId,
          topic: topic
        });

      if (error) throw error;

      const replyRecipients = collectRecipientsForTopic(
        comments,
        topic,
        candidate.email,
        evaluator.email
      );
      replyRecipients.forEach((email) => {
        sendNotification('comment', candidate.id, evaluator.name, replyContent.trim(), email);
      });
      setReplyContent('');
      setReplyingTo(null);
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const organizeComments = (comments: EvaluationComment[]) => {
    const topicGroups: { [key: string]: EvaluationComment[] } = {};
    const replies: { [key: string]: EvaluationComment[] } = {};

    comments.forEach(comment => {
      if (comment.parent_comment_id) {
        if (!replies[comment.parent_comment_id]) {
          replies[comment.parent_comment_id] = [];
        }
        replies[comment.parent_comment_id].push(comment);
      } else {
        const topic = comment.topic || 'general';
        if (!topicGroups[topic]) {
          topicGroups[topic] = [];
        }
        topicGroups[topic].push(comment);
      }
    });

    return { topicGroups, replies };
  };

  const getCommentAuthorName = (comment: EvaluationComment) => {
    if (comment.author_name) return comment.author_name;
    if (comment.user_type === 'candidate') {
      return candidate.name;
    }
    const ev = allEvaluations.find((e: any) => e.id === comment.evaluation_id);
    if (ev && evaluatorById[ev.evaluator_id]) {
      const info = evaluatorById[ev.evaluator_id];
      return info.name;
    }
    return '参加者';
  };

  const getCommentMeta = (comment: EvaluationComment): { avatarUrl?: string; university?: string; role: 'researcher' | 'evaluator' | 'reviewer' } => {
    if (comment.user_type === 'candidate') {
      return { avatarUrl: candidate.avatar_url, university: candidate.university, role: 'researcher' };
    }
    const ev = allEvaluations.find((e: any) => e.id === comment.evaluation_id);
    if (ev && evaluatorById[ev.evaluator_id]) {
      const info = evaluatorById[ev.evaluator_id];
      return { avatarUrl: info.avatar_url, university: info.university, role: info.is_reviewer ? 'reviewer' : 'evaluator' };
    }
    return { role: 'evaluator' };
  };

  const handleSetRating = async (value: number) => {
    if (isOwn) return; // 自分の研究は評価不可
    const next = Math.max(0, Math.min(5, value));
    setRating(next);
    try {
      await savePartial({ rating: next });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 800);
      // 集計の即時反映
      await loadAllEvaluations();
    } catch (e: any) {
      setError(e.message);
    }
  };
  const getParentInfo = (comment: EvaluationComment) => {
    if (!comment.parent_comment_id) return { author: undefined, content: undefined };
    const parent = comments.find(c => c.id === comment.parent_comment_id);
    if (parent) return { author: getCommentAuthorName(parent), content: parent.content };
    return { author: undefined, content: undefined };
  };

  const renderComment = (comment: EvaluationComment, allReplies: { [key: string]: EvaluationComment[] }, _level = 0) => {
    const parentInfo = getParentInfo(comment);
    const meta = getCommentMeta(comment);
    return (
    <div key={comment.id}>
      <ChatBubble
        authorName={getCommentAuthorName(comment)}
        isOwn={comment.user_id === currentUserIdRef.current}
        timestamp={comment.created_at}
        parentAuthor={parentInfo.author}
        parentContent={parentInfo.content}
        avatarUrl={meta.avatarUrl}
        university={meta.university}
        role={meta.role}
        onReply={() => setReplyingTo(comment.id)}
      >
        {comment.content}
      </ChatBubble>

      {/* Reply form */}
      {replyingTo === comment.id && (
        <div className="mb-3 ml-10 md:ml-12">
          <div className="space-y-3 p-3 bg-muted/30 rounded-lg border border-border">
            <Textarea
              placeholder="返信内容を入力..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              rows={2}
              className="resize-none text-sm"
              autoFocus
            />
            <div className="flex items-center justify-end">
              <div className="flex space-x-2">
                <Button variant="ghost" size="sm" onClick={() => { setReplyingTo(null); setReplyContent(''); }}>
                  キャンセル
                </Button>
                <Button size="sm" onClick={() => handleSubmitReply(comment.id, comment.topic)} disabled={!replyContent.trim() || commentLoading}>
                  {commentLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div> : <Send className="h-4 w-4 mr-1" />}
                  送信
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Nested replies (flat in chat style) */}
      {allReplies[comment.id]?.map(reply => renderComment(reply, allReplies, _level + 1))}
    </div>
  );
  };
  const formatCommentTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('ja-JP', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('ja-JP', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const sortedSlides = candidate.slide_images?.sort((a, b) => a.slide_index - b.slide_index) || [];

  // Simple swipe-to-go-back (right swipe) for mobile
  let touchStartX = 0, touchStartY = 0;
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    touchStartX = t.clientX;
    touchStartY = t.clientY;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX;
    const dy = Math.abs(t.clientY - touchStartY);
    if (dx > 50 && dy < 40) {
      onBack();
    }
  };

  return (
    <div className="space-y-6" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={onBack}
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          一覧に戻る
        </Button>
        
        {existingEvaluation && (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
            評価済み
          </Badge>
        )}
      </div>

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {success && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-600 mr-2" />
              <p className="text-sm text-green-600">評価を保存しました</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* アクション（上部に配置）: 自分の研究では非表示 */}
      {!isOwn && (
        <Card className="md:sticky md:top-0 z-10 bg-card/90 backdrop-blur supports-[backdrop-filter]:bg-card/80">
          <CardHeader className="hidden md:block">
            <CardTitle>アクション</CardTitle>
            <CardDescription>星評価・「いいね」「参加希望」は即保存されます。</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6 py-3 md:py-6">
            <div className="space-y-3">
              <div className="space-y-1 md:space-y-2">
                <Label className="hidden md:inline-block text-sm">星評価（0〜5、0は評価なし）</Label>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map((i) => (
                    <button
                      key={i}
                      type="button"
                      className="p-1"
                      onClick={() => handleSetRating(i)}
                      aria-label={`星${i}`}
                    >
                      <Star className={`h-5 w-5 md:h-6 md:w-6 ${rating >= i ? 'text-yellow-500 fill-current' : 'text-muted-foreground'}`} />
                    </button>
                  ))}
                </div>
                <div className="hidden md:block">
                {(() => {
                  const rated = allEvaluations.filter((e) => (e.rating || 0) > 0);
                  const count = rated.length;
                  const avg = count > 0 ? (rated.reduce((s, e) => s + Math.min(5, Math.max(0, e.rating || 0)), 0) / count) : 0;
                  return (
                    <p className="text-xs text-muted-foreground mt-1">
                      平均 {avg.toFixed(1)} / 5 ・ 評価数 {count}
                    </p>
                  );
                })()}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="liked-top"
                  checked={liked}
                  onCheckedChange={async (checked) => {
                    const next = !!checked;
                    setLiked(next);
                    setSavingLike(true);
                    setError(null);
                    try {
                      await savePartial({ liked: next });
                      // Debounced: actual notify happens after NOTIFY_DEBOUNCE_MS
                      // of inactivity, only if state is still ON.
                      scheduleNotification({ liked: next });
                    } catch (e: any) {
                      setError(e.message);
                      setLiked(!next);
                    } finally {
                      setSavingLike(false);
                    }
                  }}
                />
                <Label htmlFor="liked-top" className="flex items-center space-x-2 cursor-pointer">
                  <Heart className={`h-4 w-4 ${liked ? 'fill-current text-red-500' : 'text-muted-foreground'}`} />
                  <span>いいね！{savingLike ? '（保存中…）' : ''}</span>
                </Label>
              </div>
            </div>
            <div className="space-y-2 md:space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="participate-top"
                  checked={wantToParticipate}
                  onCheckedChange={async (checked) => {
                    const next = !!checked;
                    setWantToParticipate(next);
                    setSavingParticipate(true);
                    setError(null);
                    try {
                      await savePartial({ want_to_participate: next });
                      // Debounced: see scheduleNotification for the rationale.
                      scheduleNotification({ wantToParticipate: next });
                    } catch (e: any) {
                      setError(e.message);
                      setWantToParticipate(!next);
                    } finally {
                      setSavingParticipate(false);
                    }
                  }}
                />
                <Label htmlFor="participate-top" className="flex items-center space-x-2 cursor-pointer">
                  <Users className={`h-4 w-4 ${wantToParticipate ? 'text-green-600' : 'text-muted-foreground'}`} />
                  <span>参加希望{savingParticipate ? '（保存中…）' : ''}</span>
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Research Information (above slides) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{candidate.research_topic}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground">研究者</label>
              <p className="text-foreground font-medium">{candidate.name}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground">所属</label>
              <p className="text-foreground font-medium">{candidate.affiliation}</p>
            </div>
          </div>

          {candidate.research_overview && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground">研究概要</label>
              <p className="text-foreground whitespace-pre-wrap leading-relaxed">{candidate.research_overview}</p>
            </div>
          )}

          {(candidate as any).research_comment && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground">マッチングイベントへのコメント</label>
              <div className="bg-muted/50 rounded-lg p-4 border">
                <p className="text-foreground whitespace-pre-wrap leading-relaxed">{(candidate as any).research_comment}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Slides */}
      <Card>
        <CardHeader>
          <CardTitle>研究スライド</CardTitle>
        </CardHeader>
        <CardContent>
          {sortedSlides.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">スライドがアップロードされていません</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {sortedSlides.map((slide, idx) => (
                  <div key={slide.id} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-foreground">
                        スライド {slide.slide_index + 1}
                      </h3>
                      {slide.slide_index === candidate.key_slide_index && (
                        <Badge className="bg-yellow-500 hover:bg-yellow-600">
                          <Star className="h-4 w-4 mr-1 fill-current" />
                          キースライド
                        </Badge>
                      )}
                    </div>
                    <div
                      className="aspect-video bg-muted rounded-lg overflow-hidden border cursor-pointer"
                      onDoubleClick={() => setViewerIndex(idx)}
                    >
                      <img
                        src={slide.image_url}
                        alt={`スライド ${slide.slide_index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">画像をダブルクリックで拡大表示</p>
              {viewerIndex !== null && (
                <ImageViewer
                  images={sortedSlides.map((s) => ({ url: s.image_url, label: `スライド ${s.slide_index + 1}` }))}
                  initialIndex={viewerIndex}
                  onClose={() => setViewerIndex(null)}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Like/Participation section was already at the top */}

      {/* Like/Participation section was moved to the top (アクション) */}

      {/* Comments Section - Always show */}
      {(
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <MessageCircle className="h-5 w-5 mr-2" />
                コメント・質問
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNewCommentForm(true)}
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                新規トピック
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* New Topic Form (shown when button clicked) */}
            {showNewCommentForm && (
              <div className="space-y-3 p-4 rounded-lg border bg-muted/20 border-border">
                <div className="flex items-center justify-between">
                  <h5 className="font-semibold text-sm">新しいトピックを作成</h5>
                  <Button variant="ghost" size="sm" onClick={() => { setShowNewCommentForm(false); setNewComment(''); setNewTopic(''); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <Input
                  placeholder="トピック名（例: 研究手法について）"
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                  autoFocus
                />
                <Textarea
                  placeholder="最初のコメントを入力..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={2}
                  className="resize-none"
                />
                <div className="flex items-center justify-end">
                  <Button
                    size="sm"
                    onClick={() => { handleSubmitComment(); setShowNewCommentForm(false); }}
                    disabled={!newTopic.trim() || !newComment.trim() || commentLoading}
                  >
                    {commentLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div> : <Send className="h-4 w-4 mr-1" />}
                    作成
                  </Button>
                </div>
              </div>
            )}

            {/* Topic-based Comments */}
            <div className="space-y-4">
              {comments.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  まだコメントがありません
                </p>
              ) : (
                (() => {
                  const { topicGroups, replies } = organizeComments(comments);
                  return Object.entries(topicGroups).map(([topic, topicComments]) => {
                    const topicKey = topic === 'general' ? '' : topic;
                    return (
                    <div key={topic} className="rounded-lg shadow-sm overflow-hidden border border-border bg-card">
                      <div
                        className="px-4 py-3 border-b flex items-center justify-between cursor-pointer bg-muted/30 border-border"
                        onClick={() => setCollapsedTopics((prev) => ({ ...prev, [topic]: !prev[topic] }))}
                      >
                        <h4 className="font-semibold text-sm text-foreground flex items-center">
                          <MessageCircle className="h-4 w-4 text-primary mr-2" />
                          {topic === 'general' ? '一般' : topic}
                          <span className="ml-2 text-xs text-muted-foreground font-normal">{topicComments.length}件</span>
                        </h4>
                        <span className="text-xs text-muted-foreground">{collapsedTopics[topic] ? '展開' : '折りたたむ'}</span>
                      </div>
                      {!collapsedTopics[topic] && (
                        <>
                          <div className="p-4 space-y-1">
                            {topicComments.map(comment =>
                              renderComment(comment, replies, 0)
                            )}
                          </div>
                          <div className="px-4 py-3 border-t border-border bg-muted/10">
                            <div className="flex gap-2">
                              <Input
                                placeholder="コメントを入力..."
                                value={newTopic === topicKey ? newComment : ''}
                                onFocus={() => { setNewTopic(topicKey); }}
                                onChange={(e) => { setNewTopic(topicKey); setNewComment(e.target.value); }}
                                className="text-sm flex-1"
                              />
                              <Button
                                size="sm"
                                onClick={() => { setNewTopic(topicKey); handleSubmitComment(); }}
                                disabled={!(newTopic === topicKey && newComment.trim()) || commentLoading}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );});
                })()
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
